// src/workspace/FeatureCellPage.jsx
//
// The Feature Adjudicator: cells in a rail, one cell's groups on the right.
//
// ── WHY ONE PAGE ────────────────────────────────────────────────────────────
//
// It was two -- a cell list that navigated to a detail view -- and a reviewer
// picking a cell had to leave the list to see what was in it, then go back to
// pick another. WellFinder Review is the precedent and is already proven at this
// shape: a fixed-basis rail, a flexible detail pane, selection in a store both
// halves read.
//
// TWO LEVELS, AND ONLY ONE IS IN THE RAIL. There are 14 cells and, within a
// cell, ~25 groups. The cell is the lock unit and the imagery unit, so it is
// what the rail lists; group navigation stays on the right under j/k. Putting
// both in one list would make j ambiguous and would make the lock follow a
// keystroke.
//
// One cell, adjudicated a GROUP at a time.
//
// ── THE SLICE ───────────────────────────────────────────────────────────────
//
// Open a cell, lock it, see the triple on imagery, move between groups, reject
// with a reason and a note, release the lock. REJECT ONLY, and that is forced
// rather than chosen: until migration 081 landed, feature_assertions'
// entity_optional_chk required an entity_id on every type except defer and
// reject, and all 229 deferred observations have ZERO entity links -- that is
// why they were deferred. Confirm became writable with 081 and arrives next;
// merge and split cannot be expressed at all yet, because POST /verdicts has no
// operand list for them.
//
// ── WHY GROUPS AND NOT ROWS ─────────────────────────────────────────────────
//
// 256 of 258 rival edges are reciprocal. Walking the 229 rows shows a reviewer
// the same two polygons twice -- second time with the roles swapped -- for 84%
// of the corpus, and shows the one 24-member cluster 24 times. Grouping is 103
// stops, and it is derived client-side from payload->rival_polygons, which
// /queue already returns for the whole cell.

import { useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  useFeatureAdjudicationStore, groupProgress, decidedRivals,
} from "../stores/useFeatureAdjudicationStore";
import { useAuthStore } from "../stores/useAuthStore";
import CellMap from "../components/featureAdjudication/CellMap";
import CellRow, { classLabel } from "../components/featureAdjudication/CellRow";
import { resolveKey, REJECT_REASONS, KEY_ACTIONS, KEY_GROUPS } from "../components/featureAdjudication/featureKeys";

// Well inside the server's 30 minutes. Two refreshes are lost before a lock can
// lapse, so a single failed request never costs the lock.
const LOCK_REFRESH_MS = 8 * 60 * 1000;

export default function FeatureCellPage() {
  const { cellX, cellY } = useParams({ strict: false });
  const navigate = useNavigate();
  const myUserId = useAuthStore((st) => st.profile?.id ?? null);
  const s = useFeatureAdjudicationStore();

  const x = Number(cellX), y = Number(cellY);

  // The rail loads once for the page. reset() runs on unmount only -- it clears
  // `decided`, and `decided` is the reviewer's session.
  useEffect(() => {
    s.reset();
    s.loadCells();
    return () => { s.releaseLock(); s.reset(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A DEEP LINK SELECTS; A BARE VISIT DOES NOT. /workspace/features/$x/$y is
  // someone saying "look at this cell", which is an explicit act and takes the
  // lock. /workspace/features shows the rail with nothing selected, because
  // auto-selecting would claim the busiest cell for anyone who merely opened the
  // page.
  useEffect(() => {
    if (!Number.isInteger(x) || !Number.isInteger(y)) return;
    if (s.selectedCell === `${x}:${y}`) return;
    s.selectCell(x, y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);

  // Refresh while held. Released on unmount above; the 30-minute expiry is the
  // backstop for a tab closed without unmounting.
  useEffect(() => {
    if (s.lockState !== "held") return;
    const t = setInterval(() => s.refreshLock(), LOCK_REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.lockState]);

  const group = s.groups[s.index] ?? null;
  const subject = group?.members.find((m) => m.observation_id === s.subjectId) ?? group?.members[0] ?? null;
  const writable = s.lockState === "held";
  // A decided member takes no further verdict. The row stays visible because the
  // queue is a view and the reviewer needs to see what they settled while
  // judging its rivals -- but it is NOT actionable, which is the half of the
  // staleness story that was missing when a reviewer rejected one observation
  // twice, 11 seconds apart.
  const subjectDecided = Boolean(subject && s.decided[subject.observation_id]);
  const progress = group ? groupProgress(group, s.decided) : { total: 0, done: 0, complete: false };
  const groupComplete = progress.complete;

  // Outstanding minus what this session decided, so a cell can reach zero in the
  // rail without a refetch. Keyed by "x:y" for O(1) lookup per row.
  const decidedByCell = useMemo(() => {
    const m = {};
    for (const r of s.rows) {
      if (s.decided[r.observation_id]) m[`${r.cell_x}:${r.cell_y}`] = (m[`${r.cell_x}:${r.cell_y}`] ?? 0) + 1;
    }
    return m;
  }, [s.rows, s.decided]);

  const remaining = useMemo(
    () => s.groups.filter((g) => !groupProgress(g, s.decided).complete).length,
    [s.groups, s.decided]);

  // ── Stepping the rail ─────────────────────────────────────────────────────
  //
  // Driven by shift-J / shift-K from the ONE global handler below, not by a
  // container listener. The first version scoped j/k to the rail by focus and
  // browser review found it broken on the first action: selecting a cell clicks
  // a button inside the rail, so activeElement stays there and j/k moved cells
  // forever. Focus was an invisible mode, and the reviewer could not see which
  // meaning the next keystroke carried.
  //
  // Clamped, not wrapped. Wrapping 14 rows would move a reviewer from the
  // busiest cell to the emptiest on one keystroke, silently -- and each step
  // takes a lock, so a wrap is not a free motion.
  const stepCell = useCallback((delta) => {
    const { cells, selectedCell } = useFeatureAdjudicationStore.getState();
    if (!cells.length) return;
    const at = cells.findIndex((c) => `${c.cell_x}:${c.cell_y}` === selectedCell);
    const next = cells[Math.max(0, Math.min(cells.length - 1, at < 0 ? 0 : at + delta))];
    if (!next) return;
    navigate({
      to: "/workspace/features/$cellX/$cellY",
      params: { cellX: String(next.cell_x), cellY: String(next.cell_y) },
    });
  }, [navigate]);

  // ── Keys ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const action = resolveKey(e);
      if (!action) return;
      if (action === "cancel") {
        e.preventDefault();
        if (s.helpOpen) s.setHelpOpen(false);
        else if (s.rejectPickerOpen) s.setRejectPickerOpen(false);
        else document.activeElement?.blur?.();
        return;
      }
      if (action === "help")  { e.preventDefault(); s.setHelpOpen(!s.helpOpen); return; }
      if (action === "next")  { e.preventDefault(); s.next(); return; }
      if (action === "prev")  { e.preventDefault(); s.prev(); return; }
      if (action === "next_cell") { e.preventDefault(); stepCell(1); return; }
      if (action === "prev_cell") { e.preventDefault(); stepCell(-1); return; }
      if (action === "note")  {
        e.preventDefault();
        document.getElementById("fa-note")?.focus();
        return;
      }
      if (action === "reject") {
        e.preventDefault();
        if (writable && !subjectDecided) s.setRejectPickerOpen(true);
        return;
      }
      if (action === "confirm_one") {
        e.preventDefault();
        if (writable && !subjectDecided) s.confirmOne();
        return;
      }
      if (action === "confirm_group") {
        e.preventDefault();
        if (writable && !groupComplete) s.confirmGroup();
        return;
      }
      if (action.startsWith("digit:")) {
        e.preventDefault();
        const d = action.slice(6);
        // The digits do double duty exactly as WellFinder's do: a reason while
        // the picker is open, a group member otherwise.
        if (s.rejectPickerOpen) {
          const r = REJECT_REASONS.find((x) => x.digit === d);
          if (r && writable) s.reject(r.value);
        } else {
          const m = group?.members[Number(d) - 1];
          if (m) s.selectMember(m.observation_id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.helpOpen, s.rejectPickerOpen, group, writable, s.subjectId, s.noteDraft]);

  return (
    <div className="flex h-full w-full flex-col">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 border-b border-bg-border px-6 py-3">
        <span className="text-sm font-medium tracking-tight">Feature Adjudicator</span>
        {s.cell && (
          <>
            <span className="font-mono text-sm tabular-nums text-fg-primary">{s.cell.x}, {s.cell.y}</span>
            <span className="text-xs text-fg-secondary">
              group <span className="tabular-nums text-fg-primary">{s.groups.length ? s.index + 1 : 0}</span>
              {" of "}<span className="tabular-nums text-fg-primary">{s.groups.length}</span>
              {" · "}<span className="tabular-nums">{remaining}</span> undecided
            </span>
          </>
        )}

        <div className="ml-auto flex items-center gap-3 text-xs">
          {s.cell && s.lockState === "readonly" && (
            <span className="inline-flex items-center gap-1.5 text-amber-300">
              <Lock size={12} /> another reviewer is in this cell — read only
            </span>
          )}
          {s.cell && s.lockState === "lapsed" && (
            <span className="inline-flex items-center gap-1.5 text-amber-300">
              <AlertTriangle size={12} /> your lock lapsed and was taken — read only
            </span>
          )}
          {s.cell && s.lockState === "none" && (
            <span className="text-fg-secondary">lock unavailable — read only</span>
          )}
          {writable && <span className="text-fg-secondary">holding this cell</span>}
          <button type="button" onClick={() => s.setHelpOpen(true)}
                  className="rounded border border-bg-border px-1.5 py-0.5 text-fg-secondary hover:text-fg-primary">
            ?
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* ── Rail ─────────────────────────────────────────────────────────── */}
        {/* 20rem target, 18rem floor. NOT WellFinder's 26/24 -- those were
            measured against QueueRow's columns, and CellRow's are different. Its
            middle column carries "-7096, 2310" in mono, which must not truncate
            because it identifies the row; that is 4.95rem, and with the 2.5rem
            badge, 6.5rem lock track, two gutters and the padding the floor is
            16.70rem. 18 gives it room; 20 leaves the map 608px at a 1280 window,
            clear of its 480px floor. */}
        {/* A LIST OF BUTTONS, not a role="listbox". The listbox role promises
            arrow-key navigation within the widget, and that promise is exactly
            what created the two-meanings-for-j problem. Each row is already a
            real <button>: Tab reaches them and Enter activates them, which is
            honest about what this is and needs no invisible mode. */}
        <div
          aria-label="Adjudication cells"
          className="flex w-[20rem] min-w-[18rem] shrink-0 flex-col overflow-auto border-r border-bg-border"
        >
          {s.cellsLoading && (
            <div className="px-4 py-8 text-xs text-fg-secondary">Loading cells…</div>
          )}
          {s.cellsError && (
            <div className="px-4 py-8 text-xs text-red-400">{s.cellsError}</div>
          )}
          {!s.cellsLoading && !s.cellsError && s.cells.length === 0 && (
            <div className="px-4 py-8 text-xs text-fg-secondary">
              Nothing deferred. Entity resolution decided every observation.
            </div>
          )}
          {s.cells.map((c) => {
            const key = `${c.cell_x}:${c.cell_y}`;
            const done = (decidedByCell[key] ?? 0) >= Number(c.outstanding || 0);
            return (
              <CellRow
                key={key}
                row={c}
                selected={s.selectedCell === key}
                isMine={Boolean(myUserId) && c.locked_by === myUserId}
                done={done}
                onSelect={(r) => navigate({
                  to: "/workspace/features/$cellX/$cellY",
                  params: { cellX: String(r.cell_x), cellY: String(r.cell_y) },
                })}
              />
            );
          })}
        </div>

        {/* ── Detail ───────────────────────────────────────────────────────── */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {!s.cell ? (
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="max-w-sm text-center text-sm text-fg-secondary">
            Pick a cell. Opening one takes its lock, so another reviewer is not
            working the same polygons.
          </p>
        </div>
      ) : s.loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="animate-spin text-fg-secondary" size={18} />
        </div>
      ) : s.error ? (
        <div className="px-6 py-16"><p className="text-sm text-red-400">{s.error}</p></div>
      ) : s.groups.length === 0 ? (
        <div className="mx-auto w-full max-w-2xl px-6 py-16">
          <p className="text-sm text-fg-secondary">Nothing outstanding in this cell.</p>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* ── Map ──────────────────────────────────────────────────────── */}
          <div className="relative min-h-[420px]">
            <CellMap
              group={group}
              subjectId={s.subjectId}
              decided={s.decided}
              imagery={s.imagery}
              cellGeom={group?.members[0]?.cell_geom ?? null}
              cellKey={`${x}:${y}`}
              onSelectMember={(id) => s.selectMember(id)}
            />
            {!s.imagery && (
              <div className="absolute left-3 top-3 rounded bg-bg/80 px-2 py-1 text-[11px] text-amber-300">
                No entitled imagery covers this cell — basemap only
              </div>
            )}
          </div>

          {/* ── Panel ────────────────────────────────────────────────────── */}
          <div className="min-h-0 overflow-y-auto border-t border-bg-border lg:border-l lg:border-t-0">
            <div className="px-4 py-3">
              <p className="text-xs text-fg-secondary">
                {group.members.length === 1
                  ? "One polygon, no rival in this cell."
                  : `${group.members.length} overlapping polygons — which boundary owns the contested points?`}
              </p>

              <div className="mt-3 space-y-1.5">
                {group.members.map((m, i) => {
                  const v = s.decided[m.observation_id];
                  const isSubject = m.observation_id === s.subjectId;
                  return (
                    <button
                      key={m.observation_id}
                      type="button"
                      onClick={() => s.selectMember(m.observation_id)}
                      data-testid={`fa-member-${m.observation_id}`}
                      className={[
                        "w-full rounded border px-2.5 py-2 text-left text-xs",
                        isSubject ? "border-fuchsia-400/50 bg-fuchsia-400/[0.06]" : "border-bg-border hover:bg-white/[0.03]",
                        v ? "opacity-60" : "",
                      ].join(" ")}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-fg-primary">
                          <span className="text-fg-secondary">{i + 1}.</span>{" "}
                          {classLabel(m.class_key)}
                        </span>
                        {v && (
                          <span className="inline-flex items-center gap-1 text-fg-secondary">
                            <CheckCircle2 size={11} className="text-emerald-400" />
                            {v.type === "reject" ? `rejected · ${v.reason.replace(/_/g, " ")}` : v.type}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-fg-secondary/70">
                        {(m.candidate_points?.length ?? 0)} contested point
                        {(m.candidate_points?.length ?? 0) === 1 ? "" : "s"}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* THE STALENESS CUE, and it is the reason `decided` is keyed by
                  observation id. After rejecting A, its rival B is still in the
                  list and still names A -- without this line a reviewer judges
                  the same pair a second time. 96 pairs, so it is not a corner. */}
              {/* A GROUP RESOLVED WHOLE says so, once. Showing 23 rows each
                  noting that their rivals were decided is the same fact
                  repeated 23 times, and it reads as outstanding work. */}
              {groupComplete && (
                <p className="mt-3 rounded border border-emerald-400/25 bg-emerald-400/[0.05] px-2.5 py-2 text-[11px] text-emerald-300">
                  Nothing outstanding in this group — all {progress.total} decided.
                </p>
              )}

              {!groupComplete && subject && decidedRivals(subject, group, s.decided).length > 0 && (
                <p className="mt-3 rounded border border-emerald-400/25 bg-emerald-400/[0.05] px-2.5 py-2 text-[11px] text-fg-secondary">
                  You already decided{" "}
                  {decidedRivals(subject, group, s.decided)
                    .map(({ member, verdict }) => `${classLabel(member.class_key)} (${verdict.type})`)
                    .join(", ")}{" "}
                  in this group.
                </p>
              )}

              {/* ── Reject ─────────────────────────────────────────────────── */}
              <div className="mt-4 border-t border-bg-border pt-3">
                {!writable && (
                  <p className="mb-2 text-[11px] text-fg-secondary">
                    Read only. A lock never blocks a verdict on the server — this is
                    to stop two reviewers doing the same work.
                  </p>
                )}
                <label htmlFor="fa-note" className="block text-[11px] text-fg-secondary">
                  Note <span className="text-fg-secondary/60">(required on a rejection)</span>
                </label>
                <textarea
                  id="fa-note"
                  rows={2}
                  value={s.noteDraft}
                  onChange={(e) => s.setNoteDraft(e.target.value)}
                  disabled={!writable}
                  className="mt-1 w-full rounded border border-bg-border bg-white/[0.03] px-2 py-1.5 text-xs
                             text-fg-primary outline-none focus:border-cyan-400/50 disabled:opacity-50"
                />

                {/* CONFIRM. Two actions of different magnitude, and the
                    modifier on the larger one is the safety -- there is no undo,
                    so the group action must be harder to fire by reflex. */}
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    disabled={!writable || s.posting || subjectDecided}
                    onClick={() => s.confirmOne()}
                    className="rounded border border-emerald-400/30 px-2 py-1.5 text-xs
                               text-emerald-300 hover:bg-emerald-400/[0.08] disabled:opacity-40"
                  >
                    Confirm this <span className="text-fg-secondary">· c</span>
                  </button>
                  <button
                    type="button"
                    disabled={!writable || s.posting || groupComplete || group.members.length < 2}
                    onClick={() => s.confirmGroup()}
                    className="rounded border border-emerald-400/30 px-2 py-1.5 text-xs
                               text-emerald-300 hover:bg-emerald-400/[0.08] disabled:opacity-40"
                  >
                    Confirm all {group.members.length} <span className="text-fg-secondary">· ⇧C</span>
                  </button>
                </div>

                {!s.rejectPickerOpen ? (
                  <button
                    type="button"
                    disabled={!writable || s.posting || subjectDecided}
                    onClick={() => s.setRejectPickerOpen(true)}
                    className="mt-2 w-full rounded border border-bg-border px-2 py-1.5 text-xs
                               text-fg-primary hover:bg-white/[0.04] disabled:opacity-40"
                  >
                    Reject this polygon <span className="text-fg-secondary">· r</span>
                  </button>
                ) : (
                  <div className="mt-2 space-y-1">
                    <p className="text-[11px] text-fg-secondary">Why? A reason is required.</p>
                    {REJECT_REASONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        disabled={s.posting}
                        onClick={() => s.reject(r.value)}
                        className="flex w-full items-baseline gap-2 rounded px-2 py-1 text-left text-xs
                                   hover:bg-white/[0.05] disabled:opacity-40"
                      >
                        <span className="w-3 text-fg-secondary">{r.digit}</span>
                        <span className="text-fg-primary">{r.label}</span>
                        <span className="truncate text-[11px] text-fg-secondary/70">{r.detail}</span>
                      </button>
                    ))}
                  </div>
                )}

                {s.postError && <p className="mt-2 text-[11px] text-red-400">{s.postError}</p>}
              </div>

              <p className="mt-4 text-[11px] text-fg-secondary/70">
                j / k move between groups · digits select a polygon · ? for keys
              </p>
            </div>
          </div>
        </div>
      )}

        </div>
      </div>

      {/* ── Help ───────────────────────────────────────────────────────────── */}
      {s.helpOpen && (
        <div data-testid="fa-help" className="absolute inset-0 z-20 flex items-center justify-center bg-bg/80 p-8"
             onClick={() => s.setHelpOpen(false)}>
          <div className="max-h-full w-[30rem] overflow-auto rounded border border-bg-border bg-bg-soft p-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-sm font-medium text-fg-primary">Keyboard</h2>
              <span className="text-[11px] text-fg-secondary">Esc or ? to close · keys are inert while typing</span>
            </div>
            {KEY_GROUPS.map((g) => (
              <div key={g} className="mb-3">
                <p className="mb-1 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">{g}</p>
                {KEY_ACTIONS.filter((a) => a.group === g).map((a) => (
                  <div key={a.id} className="flex items-baseline gap-2 py-0.5 text-xs">
                    <span className="w-24 shrink-0 font-mono text-fg-secondary">{a.keys.join(" ")}</span>
                    <span className="text-fg-primary">{a.label}</span>
                    {a.detail && <span className="truncate text-[11px] text-fg-secondary/70">{a.detail}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
